import { useCallback, useEffect, useState } from "react";
import { createUseStyles } from "react-jss";
import { useQuery } from "react-query";
import { CompanyServices, EntrepotService, ProductService } from "$services";
import { Company, Warehouse } from "$types";
import { importCSV } from "$api/stockApi";
import { theme } from "$utils/theme/bataillog";
import getCompanyLastFile from "$services/File.service";
import useAuth from "$hooks/useAuth";
import { queryClient } from "$utils/queryClient";
import { CacheInvalidationAction } from "$utils/cache";

const dataHead = [
  "entreprise*",
  "ref principale*",
  "désignation*",
  "unité de mesure*",
];

const useCompanyStock = (companyId: string, contributor?: Company) => {
  const { isAdmin, token } = useAuth();
  const classes = useStyles();

  // Removed tableData state - DataTable handles the display
  const [selectedEntrepotFile, setSelectedEntrepotFile] =
    useState<any>(undefined);
  const [dataImport, setDataImport] = useState<any>(undefined);
  const [importError, setImportError] = useState<string>("");
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [selectedEntrepotId, setSelectedEntrepotId] = useState<string>("");
  const [exportImportType, setExportImportType] = useState<
    "entrepotExport" | "massImport"
  >("entrepotExport");
  const [isModalOpen, setIsModalOpen] = useState<boolean>(false);
  const [companyLastFile, setCompanyLastFile] = useState<any>(undefined);
  const [filteredWarehouses, setFilteredWarehouses] = useState([]);

  const { data: company } = useQuery(
    [CacheInvalidationAction.GET_COMPANY_BY_ID, companyId],
    () => CompanyServices.getCompanyById(companyId),
    {
      enabled: !!companyId,
      staleTime: 60 * 1000,
    }
  );

  const fecthWarehouses = useCallback(async () => {
    const filters = {
      ...(contributor
        ? { company: contributor._id }
        : companyId
        ? { company: companyId }
        : {}),
    };

    const queryKey = [CacheInvalidationAction.GET_ENTREPOTS, filters];
    let getWarehouses: any = await queryClient.getQueryData(queryKey);
    if (!getWarehouses) {
      getWarehouses = await queryClient.fetchQuery(
        queryKey,
        () => EntrepotService.getEntrepots(filters),
        { cacheTime: 5 * 60 * 1000 }
      );
      queryClient.setQueryData(queryKey, getWarehouses);
    }

    setFilteredWarehouses(
      (getWarehouses?.data || [])?.filter((w: Warehouse) => {
        const isManageable = w.isManageable !== false; // warehouses without "isManageable must be displayed too
        if (contributor) {
          return w.parentCompany === contributor._id && isManageable;
        }
        return (
          companyId === w.parentCompany ||
          w.companies.some(
            (entrepotCompany) =>
              companyId === entrepotCompany._id && entrepotCompany.isManageable
          )
        );
      })
    );
  }, [contributor, companyId]);

  useEffect(() => {
    fecthWarehouses();
  }, [fecthWarehouses]);

  const handleSelectEntrepot = useCallback(
    (id: string) => {
      if (!isAdmin()) return;
      setExportImportType("entrepotExport");
      setSelectedEntrepotId(id);
      setIsModalOpen(true);
    },
    [isAdmin]
  );

  const importStock = async (
    data: any[],
    csv: any,
    entrepotId: string,
    companyId: string
  ) => {
    const result = await importCSV(token, {
      data,
      csv,
      entrepotId,
      companyId,
    });
    return result;
  };

  const onMassImportClick = useCallback(() => {
    setExportImportType("massImport");
    setSelectedEntrepotId("");
    setIsModalOpen(true);
  }, []);

  const handleCallbackImport = async () => {
    const companyLastFileData = await getCompanyLastFile(token, companyId);
    setCompanyLastFile(companyLastFileData?.data?.data);
    // Invalidate product stock cache so the table refetches and shows the new data
    await queryClient.invalidateQueries([
      CacheInvalidationAction.GET_PRODUCT_STOCK,
    ]);
  };

  const prepareExportData = useCallback(async () => {
    const entrepotSel = filteredWarehouses?.find(
      (e: any) => e._id === selectedEntrepotId
    );

    if (entrepotSel) {
      // Check if we have data in React Query cache first
      const cacheKey = [
        CacheInvalidationAction.GET_PRODUCT_STOCK,
        { company: companyId, warehouseId: selectedEntrepotId },
      ];
      let allProducts = queryClient.getQueryData(cacheKey);

      // If not in cache, fetch all products for this company and warehouse (without pagination)
      if (!allProducts) {
        allProducts = await queryClient.fetchQuery(
          cacheKey,
          () =>
            ProductService.getProductStock({
              company: companyId,
              warehouseId: selectedEntrepotId,
            }),
          { staleTime: 5 * 60 * 1000 } // Cache for 5 minutes
        );
      }

      const products = (allProducts as any)?.data || [];

      if (products.length > 0) {
        // Prepare data for jsons2csv - same format as global export
        const exportData = products.map((p: any) => {
          // Calculate stock value
          let value = 0;

          if (p.stock && p.stock.stocks && Array.isArray(p.stock.stocks)) {
            // If we have stock details, sum them up
            value = p.stock.stocks.reduce((total: number, stock: any) => {
              return total + (stock.quantity || 0);
            }, 0);
          } else if (p.stock && p.stock.quantity !== undefined) {
            // Use stock.quantity if available
            value = p.stock.quantity || 0;
          } else if (p.totalQuantity !== undefined) {
            // Fallback to totalQuantity if available
            value = p.totalQuantity || 0;
          }

          return {
            companyName: company?.name || "",
            reference: p.reference
              ? (p.reference.startsWith("0")
                  ? `'${p.reference}'`
                  : p.reference
                ).replace(/"/g, '""')
              : "",
            designation: (p.designation || "").replace(/"/g, '""'),
            unit: (p.unit || "").replace(/"/g, '""'),
            [`qt ${entrepotSel.name}*`]: value,
          };
        });

        const dataUnitExport = [[...dataHead, `qt ${entrepotSel.name}*`]];
        exportData.forEach((item) => {
          dataUnitExport.push([
            item.companyName,
            item.reference,
            item.designation,
            item.unit,
            item[`qt ${entrepotSel.name}*`],
          ]);
        });

        return { dataUnitExport };
      }
    }
    return null;
  }, [companyId, company?.name, selectedEntrepotId, filteredWarehouses]);

  // Update company last file
  useEffect(() => {
    (async () => {
      if (companyId) {
        const companyLastFileData = await getCompanyLastFile(token, companyId);
        setCompanyLastFile(companyLastFileData?.data?.data);
      }
    })();
  }, [companyId, token]);

  // Removed table update useEffect - DataTable handles the data display

  // Update entrepot files - only set the selected entrepot file
  useEffect(() => {
    const entrepotSel = filteredWarehouses?.find(
      (e: any) => e._id === selectedEntrepotId
    );

    if (entrepotSel) {
      if (entrepotSel.importedFiles)
        entrepotSel.importedFiles = entrepotSel.importedFiles.filter(
          (imF: any) => imF.type === "single"
        );
      setSelectedEntrepotFile(entrepotSel);
    } else {
      setSelectedEntrepotFile(undefined);
    }
  }, [selectedEntrepotId, filteredWarehouses]);

  return {
    classes,
    companyLastFile,
    dataImport,
    exportImportType,
    handleCallbackImport,
    handleSelectEntrepot,
    importError,
    importStock,
    isLoading,
    isModalOpen,
    onMassImportClick,
    prepareExportData,
    companyId,
    selectedCompany: company,
    selectedEntrepotFile,
    selectedEntrepotId,
    setDataImport,
    setExportImportType,
    setImportError,
    setIsLoading,
    setIsModalOpen,
    setSelectedEntrepotFile,
  };
};

const useStyles = createUseStyles(() => ({
  btnRight: {
    "&.MuiButton-root": {
      marginLeft: theme.spacing(2),
    },
  },
  entrepotHead: {
    display: "flex",
    "& svg": {
      marginLeft: "5px",
      opacity: 0,
    },
    "&.admin:hover svg": {
      opacity: 1,
    },
    "&.admin": {
      cursor: "pointer",
      color: theme.color.red,
    },
    "&.admin.actual": {
      color: theme.color.green,
    },
  },
  modal: {
    display: "flex",
    padding: theme.spacing(1),
    alignItems: "center",
    justifyContent: "center",
  },
  noResultPlaceHolder: {
    minHeight: "420px",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  },
  paperModal: {
    flex: "0 1 500px",
    width: 500,
    maxWidth: "100%",
  },
  fileDownloader: {
    alignItems: "center",
    display: "flex",
    gap: theme.spacing(1),
    marginTop: theme.spacing(1),
  },
  filename: {
    flex: 1,
  },
  rowStock: {
    display: "flex",
    flex: 1,
  },
  rowStockEntrepot: {
    marginRight: theme.spacing(4),
  },
}));

export default useCompanyStock;
